import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { useVideo } from '../context/VideoContext';
import { useAuth } from '../context/AuthContext';
import SafeIcon from '../common/SafeIcon';
import { slugify } from '../common/utils';
import ErrorPage from './ErrorPage';
import VideoForm from '../components/admin/VideoForm';
import * as FiIcons from 'react-icons/fi';
import * as FaIcons from 'react-icons/fa';

const {
  FiArrowLeft, FiCalendar, FiBriefcase, FiLayers, FiTag, FiClock,
  FiVideo, FiShare2, FiExternalLink, FiDollarSign, FiX, FiCheckCircle,
  FiZap, FiCopy, FiCheck, FiLink, FiTrendingUp, FiMail, FiSend,
  FiLoader, FiArrowRight, FiEye, FiUsers, FiEdit2, FiBarChart2
} = FiIcons;
const { FaWhatsapp, FaSlack, FaDiscord, FaReddit } = FaIcons;

const getScoreStyle = (score) => {
  if (score >= 10) return 'text-yellow-400 drop-shadow-[0_0_15px_rgba(250,204,21,0.8)] animate-pulse';
  if (score === 9)  return 'text-slate-300 drop-shadow-[0_0_15px_rgba(203,213,225,0.8)] animate-pulse';
  if (score === 8)  return 'text-amber-500 drop-shadow-[0_0_15px_rgba(245,158,11,0.8)] animate-pulse';
  return 'text-orange-500';
};

const ProjectDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { videos, loading } = useVideo();
  const { user, role } = useAuth();

  const [video, setVideo] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [isCopied, setIsCopied] = useState(false);

  const [loginEmail, setLoginEmail] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginSent, setLoginSent] = useState(false);
  const [loginError, setLoginError] = useState('');

  const backPath = location.state?.from || '/';
  const backLabel = location.state?.label || 'Back to Gallery';

  const { signInWithMagicLink } = useAuth();

  useEffect(() => {
    if (loading) return;
    if (videos.length > 0) {
      const found = videos.find(v => v.slug === slug) ||
                    videos.find(v => slugify(v.title) === slug) ||
                    videos.find(v => v.id === slug);
      if (found) setVideo(found);
    }
    window.scrollTo(0, 0);
  }, [slug, videos, loading]);

  const handleShareClick = () => {
    navigator.clipboard.writeText(window.location.href);
    setIsCopied(true);
    setShowShareModal(true);
    setTimeout(() => setIsCopied(false), 2000);
  };

  const shareLinks = [
    { name: 'WhatsApp', icon: FaWhatsapp, color: 'hover:text-[#25D366] hover:bg-[#25D366]/10', url: `https://api.whatsapp.com/send?text=${encodeURIComponent(`Check out this video: ${video?.title} - ` + window.location.href)}` },
    { name: 'Reddit', icon: FaReddit, color: 'hover:text-[#FF4500] hover:bg-[#FF4500]/10', url: `https://www.reddit.com/submit?url=${encodeURIComponent(window.location.href)}&title=${encodeURIComponent(video?.title || '')}` },
    { name: 'Slack', icon: FaSlack, color: 'hover:text-[#4A154B] hover:bg-[#4A154B]/10', url: `https://slack.com/share?url=${encodeURIComponent(window.location.href)}` },
    { name: 'Discord', icon: FaDiscord, color: 'hover:text-[#5865F2] hover:bg-[#5865F2]/10', url: `https://discord.com/channels/@me` }
  ];

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setLoginError('');
    setLoginLoading(true);
    try {
      await signInWithMagicLink(loginEmail);
      setLoginSent(true);
    } catch (err) {
      setLoginError(err.message || 'Failed to send magic link');
    } finally {
      setLoginLoading(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-[#09090b]">
      <div className="w-12 h-12 border-4 border-orange-500/20 border-t-orange-500 rounded-full animate-spin" />
    </div>
  );

  if (!video) return <ErrorPage errorCode="404" />;

  const getEmbedUrl = (url) => {
    if (!url) return '';
    const youtubeRegex = new RegExp('(?:youtube\\.com/(?:[^/]+/.+/|(?:v|e(?:mbed)?)/|.*[?&]v=)|youtu\\.be/)([^"&?/\\s]{11})', 'i');
    const vimeoRegex = new RegExp('(?:vimeo\\.com/|player\\.vimeo\\.com/video/)([0-9]+)', 'i');
    const ytMatch = url.match(youtubeRegex);
    if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}?autoplay=1&rel=0&modestbranding=1&iv_load_policy=3`;
    const vimeoMatch = url.match(vimeoRegex);
    if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}?autoplay=1&dnt=1`;
    return url;
  };

  const videoTypeDisplay = Array.isArray(video.video_type)
    ? video.video_type.join(' ⏺ ')
    : video.video_type;

  const seoDescription = video.description ?
    (video.description.length > 160 ? video.description.substring(0, 157) + '...' : video.description) :
    `Watch the ${video.title} explainer video. Explore its visual style, production details, and estimated cost.`;

  // Impact score
  const score = Math.round(Number(video.market_score));
  const scoreStyle = getScoreStyle(score);

  // Why It Works — parse into bullet list
  const whyItWorksBullets = video.why_it_works
    ? video.why_it_works
        .split('\n')
        .map(line => line.replace(/^[-–•]\s*/, '').trim())
        .filter(line => line.length > 0)
    : [];

  return (
    <div className="min-h-screen bg-[#09090b] text-white selection:bg-orange-500/30 pb-20">
      <Helmet>
        <title>{`${video.title} | Video Examples`}</title>
        <meta name="description" content={seoDescription} />
        <meta property="og:title" content={`${video.title} | Explainer Video Inspiration`} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:type" content="video.other" />
      </Helmet>

      {/* Navigation Header */}
      <nav className="fixed top-0 inset-x-0 z-50 bg-[#09090b]/80 backdrop-blur-xl border-b border-white/5">
        <div className="container mx-auto px-6 py-4 flex justify-between items-center">
          <button onClick={() => navigate(backPath)} className="group flex items-center gap-3 text-slate-400 hover:text-white transition-colors">
            <div className="p-2 rounded-xl bg-white/5 group-hover:bg-orange-500/10 group-hover:text-orange-500 transition-all">
              <SafeIcon icon={FiArrowLeft} />
            </div>
            <span className="font-bold uppercase tracking-widest text-xs">{backLabel}</span>
          </button>

          <div className="flex items-center gap-4">
            <button
              onClick={handleShareClick}
              className="p-3 rounded-xl bg-white/5 hover:bg-white/10 text-slate-400 hover:text-orange-500 transition-all border border-white/5 hover:border-orange-500/30"
            >
              <SafeIcon icon={FiShare2} />
            </button>
            <a
              href="https://www.whatastory.agency/contact-us"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary py-2.5 px-6 flex items-center gap-2 text-sm"
            >
              <SafeIcon icon={FiExternalLink} />
              Get a video like this
            </a>
          </div>
        </div>
      </nav>

      <main className="container mx-auto px-6 pt-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Left Column */}
          <div className="lg:col-span-8 space-y-8">
            <motion.div
              className="relative aspect-video rounded-[32px] overflow-hidden bg-zinc-950 border border-white/5 shadow-2xl"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
            >
              <iframe
                src={getEmbedUrl(video.video_link)}
                className="w-full h-full"
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
                title={video.title}
              />
            </motion.div>

            <motion.div
              className="space-y-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <div className="space-y-6">
                <div className="flex flex-wrap items-center gap-3">
                  <span className="px-4 py-1.5 bg-orange-500/10 text-orange-500 border border-orange-500/20 rounded-full text-xs font-black uppercase tracking-widest">{videoTypeDisplay}</span>
                  <span className="px-4 py-1.5 bg-zinc-900 text-slate-400 border border-white/5 rounded-full text-xs font-black uppercase tracking-widest">{video.industry}</span>
                  <div className="flex items-center gap-2 px-4 py-1.5 bg-zinc-900/50 border border-white/5 rounded-full ml-auto md:ml-0">
                    <span className="text-xs font-bold text-slate-400 flex items-center gap-1">
                      <SafeIcon icon={FiEye} className="text-slate-500" />
                      {video.views}
                    </span>
                  </div>
                </div>

                {/* Title row — with admin Edit button */}
                <div className="flex items-start gap-4">
                  <h1 className="flex-1 text-4xl md:text-6xl font-black tracking-tight leading-tight">{video.title}</h1>
                  {role === 'admin' && (
                    <button
                      onClick={() => setIsEditing(true)}
                      title="Edit Video"
                      className="mt-2 flex-shrink-0 flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white/5 hover:bg-orange-500/10 border border-white/10 hover:border-orange-500/30 text-slate-400 hover:text-orange-500 transition-all text-sm font-bold"
                    >
                      <SafeIcon icon={FiEdit2} />
                      Edit
                    </button>
                  )}
                </div>

                {/* Project Brief */}
                <div className="p-8 bg-zinc-900/50 rounded-[32px] border border-white/5">
                  <h3 className="text-white/30 text-xs font-black uppercase tracking-[0.2em] mb-4">Project Brief</h3>
                  <p className="text-xl text-slate-300 leading-relaxed font-medium">{video.description || "A premium creative project curated for the collection."}</p>
                </div>

                {/* Why This Video Works — bullet list */}
                {whyItWorksBullets.length > 0 && (
                  <div className="p-8 bg-zinc-900/50 rounded-[32px] border border-white/5">
                    <h3 className="text-white/30 text-xs font-black uppercase tracking-[0.2em] mb-6">Why This Video Works</h3>
                    <ul className="space-y-4">
                      {whyItWorksBullets.map((line, i) => (
                        <li key={i} className="flex items-start gap-3">
                          <SafeIcon icon={FiCheckCircle} className="text-orange-500 mt-1 flex-shrink-0" />
                          <span className="text-lg text-slate-300 leading-relaxed font-medium">{line}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <motion.a
                  href="https://www.whatastory.agency/resources/ai-quote?source=videoexamples.com&button=videopage"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-3 px-8 py-4 bg-orange-500/10 hover:bg-orange-500/20 border border-orange-500/20 hover:border-orange-500/50 rounded-2xl text-orange-500 font-black uppercase tracking-widest text-sm transition-all group shadow-lg shadow-orange-500/5"
                  whileHover={{ x: 5 }}
                >
                  <SafeIcon icon={FiZap} className="text-lg" />
                  Get Instant Quote for a video like this
                  <SafeIcon icon={FiExternalLink} className="text-base opacity-50 group-hover:translate-x-1 transition-transform" />
                </motion.a>
              </div>
            </motion.div>
          </div>

          {/* Right Column - Sidebar */}
          <div className="lg:col-span-4 space-y-6">
            <motion.div
              className="glass-card p-8 border-white/5 sticky top-32"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
            >
              <div className="flex justify-between items-start mb-8">
                <h3 className="text-lg font-bold flex items-center gap-2">
                  <div className="w-1.5 h-6 bg-orange-500 rounded-full" />
                  Project Details
                </h3>
                {score > 0 && (
                  <div className="text-right">
                    <span className="text-[10px] text-white/40 font-black uppercase tracking-widest block mb-1">Impact Score</span>
                    <div className="text-3xl font-black leading-none flex items-center justify-end gap-1.5">
                      <SafeIcon icon={FiTrendingUp} className={scoreStyle} />
                      <span className={scoreStyle}>{score}</span>
                    </div>
                  </div>
                )}
              </div>

              <div className="space-y-8">
                <DetailItem icon={FiBriefcase} label="Company" value={video.client} />
                {video.audience && (
                  <DetailItem icon={FiUsers} label="Audience" value={video.audience} />
                )}
                <DetailItem icon={FiLayers} label="Visual Styles" value={Array.isArray(video.visual_style) ? video.visual_style.join(', ') : video.visual_style} />
                <DetailItem icon={FiClock} label="Duration" value={video.duration} />
                <DetailItem icon={FiCalendar} label="Release Date" value={new Date(video.published_on).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })} />

                {/* Estimated Production Cost — visible to all visitors */}
                {video.estimated_cost && (
                  <DetailItem icon={FiDollarSign} label="Estimated Production Cost" value={`$${Number(video.estimated_cost).toLocaleString()}`} />
                )}

                {/* Find the best price CTA */}
                <a
                  href="https://www.whatastory.agency/resources/ai-quote?source=videoexamples.com&button=sidebar"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full flex items-center justify-center gap-2 py-3 px-4 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 rounded-2xl text-slate-400 hover:text-white text-sm font-bold transition-all"
                >
                  <SafeIcon icon={FiBarChart2} className="text-orange-500/70" />
                  Find the best price
                </a>

                <div className="pt-8 border-t border-white/5">
                  <h4 className="text-white/30 text-[10px] font-black uppercase tracking-[0.2em] mb-4">Keywords</h4>
                  <div className="flex flex-wrap gap-2">
                    {video.tags?.map(tag => (
                      <span key={tag} className="px-3 py-1.5 bg-white/5 hover:bg-white/10 border border-white/5 rounded-xl text-xs text-slate-400 transition-colors cursor-default flex items-center gap-1.5">
                        <SafeIcon icon={FiTag} className="text-[10px]" />
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </main>

      {/* Share Modal */}
      <AnimatePresence>
        {showShareModal && (
          <div className="fixed inset-0 z-[110] flex items-center justify-center p-6">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/90 backdrop-blur-sm"
              onClick={() => setShowShareModal(false)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="relative w-full max-w-md glass-card p-8 border-white/10 overflow-hidden"
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-orange-500/10 blur-[60px] -z-10" />
              <button onClick={() => setShowShareModal(false)} className="absolute top-6 right-6 text-white/40 hover:text-white transition-colors"><SafeIcon icon={FiX} /></button>

              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-orange-500/10 rounded-2xl flex items-center justify-center mx-auto mb-4 border border-orange-500/20">
                  <SafeIcon icon={FiShare2} className="text-2xl text-orange-500" />
                </div>
                <h3 className="text-2xl font-black text-white mb-2">Share Project</h3>
                <p className="text-slate-400 text-sm">The project link has been copied to your clipboard.</p>
              </div>

              <div className="relative mb-8 p-4 bg-zinc-950/50 border border-white/10 rounded-2xl flex items-center gap-3">
                <SafeIcon icon={FiLink} className="text-orange-500/50 shrink-0" />
                <div className="text-xs text-slate-400 font-mono truncate flex-1">{window.location.href}</div>
                <div className="flex items-center gap-2">
                  <AnimatePresence>
                    {isCopied && <motion.span initial={{ opacity: 0, x: 5 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0 }} className="text-[10px] font-bold text-green-400 uppercase">Copied!</motion.span>}
                  </AnimatePresence>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(window.location.href);
                      setIsCopied(true);
                      setTimeout(() => setIsCopied(false), 2000);
                    }}
                    className="p-2 hover:bg-white/5 rounded-lg text-slate-400 hover:text-white transition-all"
                  >
                    <SafeIcon icon={isCopied ? FiCheck : FiCopy} />
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {shareLinks.map((link) => (
                  <a
                    key={link.name}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`flex items-center gap-3 p-4 bg-white/5 border border-white/10 rounded-2xl transition-all duration-300 group ${link.color}`}
                  >
                    <link.icon className="text-xl group-hover:scale-110 transition-transform" />
                    <span className="text-sm font-bold">{link.name}</span>
                  </a>
                ))}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Login Modal */}
      <AnimatePresence>
        {showLoginModal && (
          <div className="fixed inset-0 z-[120] flex items-center justify-center p-6">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/90 backdrop-blur-md"
              onClick={() => setShowLoginModal(false)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="relative w-full max-w-md glass-card p-8 border-white/10"
            >
              <button onClick={() => setShowLoginModal(false)} className="absolute top-6 right-6 text-white/40 hover:text-white transition-colors"><SafeIcon icon={FiX} /></button>

              <AnimatePresence mode="wait">
                {!loginSent ? (
                  <motion.div key="form" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                    <div className="text-center mb-8">
                      <h3 className="text-2xl font-black text-white mb-2">Unlock Pricing</h3>
                      <p className="text-slate-400 text-sm">Enter your email to sign up or sign in and reveal project costs.</p>
                    </div>

                    <form onSubmit={handleLoginSubmit} className="space-y-6">
                      <div className="space-y-2">
                        <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Email Address</label>
                        <div className="relative">
                          <SafeIcon icon={FiMail} className="absolute left-4 top-3.5 text-slate-500" />
                          <input
                            type="email"
                            required
                            value={loginEmail}
                            onChange={(e) => setLoginEmail(e.target.value)}
                            className="w-full bg-zinc-900/50 border border-white/10 rounded-2xl pl-12 pr-4 py-3 text-white focus:border-orange-500 outline-none transition-all"
                            placeholder="you@example.com"
                          />
                        </div>
                      </div>

                      {loginError && (
                        <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 text-sm text-center">
                          {loginError}
                        </div>
                      )}

                      <button
                        type="submit"
                        disabled={loginLoading}
                        className="w-full btn-primary py-3.5 flex items-center justify-center gap-2"
                      >
                        {loginLoading ? (
                          <SafeIcon icon={FiLoader} className="animate-spin text-xl" />
                        ) : (
                          <>
                            <SafeIcon icon={FiSend} />
                            Send Magic Link
                          </>
                        )}
                      </button>
                    </form>
                  </motion.div>
                ) : (
                  <motion.div key="success" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="text-center py-8">
                    <div className="w-20 h-20 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-6 text-green-500">
                      <SafeIcon icon={FiCheckCircle} className="text-4xl" />
                    </div>
                    <h3 className="text-2xl font-bold text-white mb-3">Check your inbox!</h3>
                    <p className="text-slate-400 mb-8 text-sm">
                      We've sent a magic link to <span className="text-white font-medium">{loginEmail}</span>.<br />
                      Click the link to reveal pricing instantly.
                    </p>
                    <button
                      onClick={() => setLoginSent(false)}
                      className="text-xs text-orange-500 hover:text-orange-400 font-bold flex items-center justify-center gap-2 mx-auto uppercase tracking-widest"
                    >
                      <SafeIcon icon={FiArrowRight} className="rotate-180" />
                      Use a different email
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Inline Video Edit Modal — admin only */}
      {isEditing && (
        <div className="fixed inset-0 z-[130] flex items-start justify-center p-6 pt-24 overflow-y-auto">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/90 backdrop-blur-md"
            onClick={() => setIsEditing(false)}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="relative w-full max-w-4xl z-10"
            onClick={(e) => e.stopPropagation()}
          >
            <VideoForm video={video} onClose={() => setIsEditing(false)} />
          </motion.div>
        </div>
      )}
    </div>
  );
};

const DetailItem = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-4">
    <div className="p-2.5 rounded-xl bg-white/5 text-orange-500/60 mt-1"><SafeIcon icon={Icon} /></div>
    <div>
      <span className="text-white/30 text-[10px] font-black uppercase tracking-[0.2em] block mb-1">{label}</span>
      <span className="text-white font-bold leading-tight block">{value || 'Not Specified'}</span>
    </div>
  </div>
);

export default ProjectDetail;